import React from "react";

/**
 * A custom hook that manages a loading state with a delay.
 * Prevents flickering of the loading spinner for fast loads.
 */
export function useDelayedLoading(
  initialLoading: boolean,
  delayMs = 500
): [boolean, (loading: boolean) => void] {
  const [isLoading, setIsLoading] = React.useState(initialLoading);
  const loadingTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  const updateLoading = React.useCallback(
    (nextLoading: boolean) => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }

      if (nextLoading) {
        loadingTimerRef.current = setTimeout(() => {
          setIsLoading(true);
          loadingTimerRef.current = null;
        }, delayMs);
      } else {
        setIsLoading(false);
      }
    },
    [delayMs]
  );

  React.useEffect(() => {
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, []);

  return [isLoading, updateLoading];
}
